import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAuth } from '../../utils/authDebug';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Alert,
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PersonAdd as AddUserIcon,
  Assessment as ReportsIcon,
  Security as AuditIcon,
  Business as CompanyIcon,
  People as UsersIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  TrendingUp as TrendingIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import GlobalInventoryAudit from '../internal/GlobalInventoryAudit';
import { adminAPI } from '../../services/api';

const AdminDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    organization: 'BARE',
    userType: 'INTERNAL'
  });

  // Check auth on mount
  useEffect(() => {
    try {
      const auth = checkAuth();
      if (!auth.token) {
        console.warn('⚠️ No auth token found - user needs to log in');
        // Don't show alert or redirect automatically - let them try and get a better error message
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    }
  }, []);

  const handleCreateUser = async () => {
    try {
      // Check auth before attempting
      const auth = checkAuth();
      if (!auth.token) {
        alert('❌ Authentication Error\n\nYou are not logged in. Please log in and try again.');
        navigate('/login');
        return;
      }

      console.log('✅ Auth token found:', auth.token.substring(0, 50) + '...');
      console.log('Creating user:', newUser);
      
      // Generate a temporary password since we'll switch to email codes soon
      const tempPassword = `Temp${Math.random().toString(36).slice(2)}!`;
      
      const userData = {
        ...newUser,
        password: tempPassword, // Temporary until email verification is implemented
        userType: 'INTERNAL'
      };
      
      const response = await adminAPI.createUser(userData);
      console.log('User created successfully:', response);
      
      alert(`✅ User created: ${newUser.email}\n\nVerification code sent via email!`);
      
      // Reset form
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        organization: 'BARE',
        userType: 'INTERNAL'
      });
      
      // Refresh the page to show new user
      window.location.reload();
      
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.message.includes('Authentication') || error.message.includes('Unauthorized')) {
        alert(`❌ Authentication Error\n\n${error.message}\n\nPlease log in again.`);
        navigate('/login');
      } else {
        alert(`❌ Error creating user: ${error.message}`);
      }
    }
  };

  // Mock data - will be replaced with API calls
  const mockStats = {
    bare: {
      totalOrders: 45,
      revenue: 28750.00,
      clients: 12,
      inventory: 250
    },
    epl: {
      totalOrders: 67,
      revenue: 42300.00,
      clients: 18,
      inventory: 380
    }
  };

  const mockUsers = [
    { id: 1, name: 'John Smith', email: 'warehouse@barebiologics.com', organization: 'BARE', role: 'Warehouse Manager', status: 'Active' },
    { id: 2, name: 'Jane Williams', email: 'warehouse@epl.com', organization: 'EPL', role: 'Warehouse Manager', status: 'Active' }
  ];

  const mockAuditLogs = [
    { id: 1, timestamp: '2024-10-02 10:30 AM', user: 'John Smith', action: 'ORDER_PROCESSED', org: 'BARE', details: 'Completed order ORD-2024-045' },
    { id: 2, timestamp: '2024-10-02 10:15 AM', user: 'Jane Williams', action: 'INVENTORY_RECEIVED', org: 'EPL', details: 'Received PO-2024-015' },
    { id: 3, timestamp: '2024-10-02 09:45 AM', user: 'Admin User', action: 'USER_CREATED', org: 'BOTH', details: 'Created user for EPL' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f7f8' }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        color: 'white',
        py: 4,
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                Admin Dashboard
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Super Admin Control Center - Bare Biologics & EPL
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/org-select')}
              sx={{
                borderColor: 'white',
                color: 'white',
                whiteSpace: 'nowrap',
                px: 6,
                borderRadius: '9999px',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Back to Organization Select
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Quick Stats - Cross-Company Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Cross-Company Overview
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#039582', mb: 0.5 }}>
                      {mockStats.bare.totalOrders + mockStats.epl.totalOrders}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Orders (Both Orgs)
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#ccfbf1' }}>
                    <OrdersIcon sx={{ color: '#039582' }} />
                  </Avatar>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">BARE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.bare.totalOrders}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">EPL</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.epl.totalOrders}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981', mb: 0.5 }}>
                      ${((mockStats.bare.revenue + mockStats.epl.revenue) / 1000).toFixed(1)}k
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Combined Revenue
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#d1fae5' }}>
                    <TrendingIcon sx={{ color: '#10b981' }} />
                  </Avatar>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">BARE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>${(mockStats.bare.revenue / 1000).toFixed(1)}k</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">EPL</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>${(mockStats.epl.revenue / 1000).toFixed(1)}k</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#3b82f6', mb: 0.5 }}>
                      {mockStats.bare.clients + mockStats.epl.clients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Clients
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#dbeafe' }}>
                    <UsersIcon sx={{ color: '#3b82f6' }} />
                  </Avatar>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">BARE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.bare.clients}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">EPL</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.epl.clients}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b', mb: 0.5 }}>
                      {mockStats.bare.inventory + mockStats.epl.inventory}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Inventory Units
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#fef3c7' }}>
                    <InventoryIcon sx={{ color: '#f59e0b' }} />
                  </Avatar>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">BARE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.bare.inventory}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">EPL</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockStats.epl.inventory}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: '#f7f7f8',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.92rem', md: '1rem' },
                minWidth: 'auto',
                px: { xs: 2, md: 3 },
                whiteSpace: 'nowrap',
                gap: 1
              },
              '& .Mui-selected': {
                color: '#039582'
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#039582'
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.35 }
            }}
          >
            <Tab label="User Management" icon={<AddUserIcon />} iconPosition="start" />
            <Tab label="System Audit Logs" icon={<AuditIcon />} iconPosition="start" />
            <Tab label="Cross-Company Analytics" icon={<ReportsIcon />} iconPosition="start" />
            <Tab label="All Users" icon={<UsersIcon />} iconPosition="start" />
            <Tab label="Global Inventory Audit" icon={<InventoryIcon />} iconPosition="start" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: 4 }}>
            {/* Tab 0: Create Internal User */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Create Internal User
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create new internal users for Bare Biologics, EPL, or super admins with access to both organizations.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>🔐 Passwordless Authentication</strong><br />
                  No password needed! Users will receive a 6-digit verification code via email to login. The code expires in 10 minutes for security.
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      placeholder="user@barebiologics.com"
                      helperText="🔐 Passwordless login - User will receive a verification code via email"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={newUser.firstName}
                      onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={newUser.lastName}
                      onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Organization Access</InputLabel>
                      <Select
                        value={newUser.organization}
                        onChange={(e) => setNewUser({ ...newUser, organization: e.target.value })}
                        label="Organization Access"
                      >
                        <MenuItem value="BARE">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="BARE" size="small" sx={{ bgcolor: '#5c725c', color: 'white' }} />
                            Bare Biologics Only
                          </Box>
                        </MenuItem>
                        <MenuItem value="EPL">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="EPL" size="small" sx={{ bgcolor: '#039582', color: 'white' }} />
                            EPL Only
                          </Box>
                        </MenuItem>
                        <MenuItem value="BOTH">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="SUPER ADMIN" size="small" sx={{ bgcolor: '#dc2626', color: 'white' }} />
                            Both Organizations (Super Admin)
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>User Type</InputLabel>
                      <Select
                        value={newUser.userType}
                        onChange={(e) => setNewUser({ ...newUser, userType: e.target.value })}
                        label="User Type"
                      >
                        <MenuItem value="INTERNAL">Internal User</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Organization Access Levels:
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Bare Only:</strong> Can access Bare Biologics operations only (Lipoderma™ products)
                      </Typography>
                      <Typography variant="body2">
                        • <strong>EPL Only:</strong> Can access EPL operations only (equicent™ CTM products)
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Super Admin:</strong> Can access both organizations and admin settings
                      </Typography>
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<AddUserIcon />}
                      onClick={handleCreateUser}
                      sx={{ 
                        bgcolor: '#039582', 
                        '&:hover': { bgcolor: '#027A6B' },
                        py: 1.5,
                        px: 4
                      }}
                      disabled={!newUser.email || !newUser.firstName || !newUser.lastName}
                    >
                      Create Internal User
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 1: Audit Logs */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      System Audit Logs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete activity trail across both Bare Biologics and EPL
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search logs..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                {isMobile ? (
                  <Stack spacing={1.5} sx={{ p: 1 }}>
                    {mockAuditLogs.map((log) => (
                      <Paper key={log.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">{log.timestamp}</Typography>
                          <Chip 
                            label={log.org} 
                            size="small"
                            sx={{ 
                              bgcolor: log.org === 'BARE' ? '#5c725c' : log.org === 'EPL' ? '#039582' : '#6b7280',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{log.user}</Typography>
                        <Box sx={{ mb: 1 }}>
                          <Chip label={log.action} size="small" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {log.details}
                        </Typography>
                        <IconButton size="small" sx={{ color: '#039582' }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mockAuditLogs.map((log) => (
                          <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f7f7f8' } }}>
                            <TableCell>{log.timestamp}</TableCell>
                            <TableCell>{log.user}</TableCell>
                            <TableCell>
                              <Chip label={log.action} size="small" sx={{ fontWeight: 600 }} />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={log.org} 
                                size="small"
                                sx={{ 
                                  bgcolor: log.org === 'BARE' ? '#5c725c' : log.org === 'EPL' ? '#039582' : '#6b7280',
                                  color: 'white'
                                }}
                              />
                            </TableCell>
                            <TableCell>{log.details}</TableCell>
                            <TableCell>
                              <IconButton size="small">
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Tab 2: Cross-Company Analytics */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Cross-Company Analytics & Reporting
                </Typography>

                {/* Comparison Charts */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ bgcolor: '#f7f7f8' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          Bare Biologics Performance
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><OrdersIcon sx={{ color: '#5c725c' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.bare.totalOrders} Orders`}
                              secondary="Total orders processed"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><TrendingIcon sx={{ color: '#10b981' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`$${mockStats.bare.revenue.toLocaleString()}`}
                              secondary="Total revenue"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><UsersIcon sx={{ color: '#3b82f6' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.bare.clients} Clients`}
                              secondary="Active client accounts"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><InventoryIcon sx={{ color: '#f59e0b' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.bare.inventory} Units`}
                              secondary="Current inventory"
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ bgcolor: '#f7f7f8' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          EPL Performance
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><OrdersIcon sx={{ color: '#039582' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.epl.totalOrders} Orders`}
                              secondary="Total orders processed"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><TrendingIcon sx={{ color: '#10b981' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`$${mockStats.epl.revenue.toLocaleString()}`}
                              secondary="Total revenue"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><UsersIcon sx={{ color: '#3b82f6' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.epl.clients} Clients`}
                              secondary="Active client accounts"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><InventoryIcon sx={{ color: '#f59e0b' }} /></ListItemIcon>
                            <ListItemText 
                              primary={`${mockStats.epl.inventory} Units`}
                              secondary="Current inventory"
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tab 3: All Users */}
            {activeTab === 3 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      All Internal Users
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage all internal users across both organizations
                    </Typography>
                  </Box>
                </Box>
                
                {isMobile ? (
                  <Stack spacing={1.5} sx={{ p: 1 }}>
                    {mockUsers.map((user) => (
                      <Paper key={user.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          </Box>
                          <Chip label={user.status} size="small" color="success" />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Chip 
                            label={user.organization} 
                            size="small"
                            sx={{ 
                              bgcolor: user.organization === 'BARE' ? '#5c725c' : '#039582',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">{user.role}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" sx={{ color: '#039582' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" sx={{ color: '#dc2626' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mockUsers.map((user) => (
                          <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f7f7f8' } }}>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip 
                                label={user.organization} 
                                size="small"
                                sx={{ 
                                  bgcolor: user.organization === 'BARE' ? '#5c725c' : '#039582',
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell>{user.role}</TableCell>
                            <TableCell>
                              <Chip label={user.status} size="small" color="success" />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small">
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={{ color: '#dc2626' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Tab 4: Global Inventory Audit */}
            {activeTab === 4 && <GlobalInventoryAudit />}
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminDashboard;

